import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { APP_BUILD_NUMBER, APP_VERSION, AUTH_TOKEN } from '@shared/consts';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UIOptions {
  froalaKey: string;
  scaytCustomerId: string;
  azureMapsSubscriptionKey: string;
  pspdfkitUrl: string;
  pspdfkitLicenseKey: string;
  appVersion: string;
  appBuildNumber: string;
  appStoreUrl: string;
  playStoreUrl: string;
  androidPackageName: string;
  isAiEnabled: boolean;
  environmentName?: string;
  aiChatSoftTimeoutSeconds?: number;
  aiChatTimeoutExtensionSeconds?: number;
}

@Injectable({
  providedIn: 'root',
})
export class EnvironmentConfigService {
  private static readonly ENV_DEV = 'Dev';
  private static readonly ENV_QA = 'QA';
  private static readonly ENV_PROD = 'Prod';

  private http = inject(HttpClient);
  private readonly aiEnabled = signal(environment.isAiEnabled);
  private readonly environmentName = signal(environment.environmentName);
  readonly isAiEnabled = this.aiEnabled.asReadonly();
  readonly isProductionEnvironment = computed(() => this.isEnvironment(EnvironmentConfigService.ENV_PROD));
  readonly isDevelopmentEnvironment = computed(() => this.isEnvironment(EnvironmentConfigService.ENV_DEV));
  readonly isQAEnvironment = computed(() => this.isEnvironment(EnvironmentConfigService.ENV_QA));

  private isConfigLoaded = false;
  private debug = false;

  async loadConfig(): Promise<void> {
    if (this.isConfigLoaded) {
      // If the configuration is already loaded, skip reloading
      return;
    }

    // Check if the token is already in local storage
    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      return;
    }

    // Make the API call to fetch the environment configuration
    return firstValueFrom(this.readConfiguration())
      .then(config => {
        // override environment variables with config from API
        Object.assign(environment, config);
        this.updateAiEnabled(config.isAiEnabled);
        this.updateEnvironmentName(config.environmentName);

        if (this.debug) {
          console.log('Environment configuration loaded and set', environment);
        }

        // this configuration is read when user is refreshing the page or adding a new tab/browser window
        // so we have to update the local app version and build number in case they have changed since the last load
        localStorage.setItem(APP_VERSION, config.appVersion ?? '');
        localStorage.setItem(APP_BUILD_NUMBER, config.appBuildNumber ?? '');
      })
      .catch(error => {
        console.error('Failed to load environment configuration', error);
        throw error;
      });
  }

  readConfiguration() {
    const token = localStorage.getItem(AUTH_TOKEN);
    return this.http.get<UIOptions>(environment.baseApiUrl + '_api/main/ui-configuration', {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  isLoaded(): boolean {
    return this.isConfigLoaded;
  }

  updateAiEnabled(enabled: boolean): void {
    environment.isAiEnabled = enabled;
    this.aiEnabled.set(enabled);
  }

  updateEnvironmentName(environmentName: string): void {
    environment.environmentName = environmentName;
    this.environmentName.set(environmentName);
  }

  private isEnvironment(environmentName: string): boolean {
    const currentEnv = this.environmentName();
    return (
      !!environmentName?.trim() && !!currentEnv?.trim() && currentEnv.toLowerCase() === environmentName.toLowerCase()
    );
  }
}
